import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Error } from "@ui/error/Error";
import Panel from "@ui/panel/Panel";
import { Select } from "@ui/select/Select";
import { Typo } from "@ui/text/Typo";
import Signal from "badmfck-signal";
import { useEffect, useState } from "react";
import { GD } from "src/GD";

interface TUserEditorProps {
	onSuccess: () => void | Promise<void>;
	userId: number;
}

export const S_USER_EDITOR_POPUP = new Signal<TUserEditorProps | null>("S_USER_EDITOR_POPUP");

type TUserRoleOption = {
	id: number;
	role: string;
	description?: string;
	displayName: string;
};

type TUserRolesForm = Record<string, unknown> & {
	user_id?: number;
	user?: {
		name?: string;
		email?: string;
	};
	roles?: unknown;
	$__roles_possible_values?: unknown;
};

const normalizeRoles = (value: unknown): TUserRoleOption[] => {
	if (!Array.isArray(value)) return [];

	return value.flatMap(item => {
		if (!item || typeof item !== "object") return [];

		const role = item as Record<string, unknown>;
		const id = Number(role.id);
		const roleName = typeof role.role === "string" ? role.role : typeof role.name === "string" ? role.name : "";
		if (!Number.isFinite(id) || !roleName) return [];

		return [
			{
				id,
				role: roleName,
				description: typeof role.description === "string" ? role.description : undefined,
				displayName: roleName
					.split("_")
					.filter(Boolean)
					.map(part => part.charAt(0).toUpperCase() + part.slice(1))
					.join(" "),
			},
		];
	});
};

const mergeRoleOptions = (possibleRoles: TUserRoleOption[], assignedRoles: TUserRoleOption[]) => {
	const rolesByID = new Map<number, TUserRoleOption>();
	for (const role of [...possibleRoles, ...assignedRoles]) rolesByID.set(role.id, role);
	return [...rolesByID.values()];
};

export const UserEditor = () => {
	useLocale();
	const request = S_USER_EDITOR_POPUP.use();
	const [form, setForm] = useState<TUserRolesForm | null>(null);
	const [roleOptions, setRoleOptions] = useState<TUserRoleOption[]>([]);
	const [selectedRole, setSelectedRole] = useState<TUserRoleOption | null>(null);
	const [initialRole, setInitialRole] = useState<TUserRoleOption | null>(null);
	const [assignedRolesCount, setAssignedRolesCount] = useState(0);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<IError>();

	useEffect(() => {
		if (!request) return;

		let cancelled = false;
		setBusy(true);
		setError(undefined);
		setForm(null);
		setRoleOptions([]);
		setSelectedRole(null);
		setInitialRole(null);
		setAssignedRolesCount(0);

		GD.REQ_API.request({
			method: "users/roles/get",
			data: { user_id: request.userId },
		}).then(response => {
			if (cancelled) return;
			setBusy(false);

			if (response.error) {
				setError(response.error);
				return;
			}

			if (!response.data || typeof response.data !== "object") {
				setError({ code: -1, message: msg("rolesResponseIsEmpty") });
				return;
			}

			const nextForm = response.data as TUserRolesForm;
			const assignedRoles = normalizeRoles(nextForm.roles);
			const options = mergeRoleOptions(normalizeRoles(nextForm.$__roles_possible_values), assignedRoles);
			const currentRole = assignedRoles.length === 1 ? options.find(role => role.id === assignedRoles[0].id) ?? assignedRoles[0] : null;

			setForm(nextForm);
			setRoleOptions(options);
			setSelectedRole(currentRole);
			setInitialRole(currentRole);
			setAssignedRolesCount(assignedRoles.length);
		});

		return () => {
			cancelled = true;
		};
	}, [request?.userId]);

	if (!request) return null;

	const closePopup = () => {
		S_USER_EDITOR_POPUP.invoke(null);
	};

	const userLabel = request.userId !== 0 ? msg("templateUser_039247f9", { v0: request.userId }) : msg("newUser");
	const userName = form?.user?.name?.trim() || userLabel;
	const userEmail = form?.user?.email?.trim();
	const initials = userName
		.split(/\s+/)
		.map(part => part.charAt(0))
		.join("")
		.slice(0, 2)
		.toUpperCase();
	const hasRoleChanged = selectedRole?.id !== initialRole?.id;

	const saveRole = async () => {
		if (!form || !selectedRole) {
			setError({ code: 0, message: msg("selectOneRoleBeforeSaving") });
			return;
		}

		setBusy(true);
		setError(undefined);
		const response = await GD.REQ_API.request({
			method: "users/roles/set",
			data: {
				...form,
				roles: [{ id: selectedRole.id, role: selectedRole.role, description: selectedRole.description }],
			},
		});
		setBusy(false);

		if (response.error) {
			setError(response.error);
			return;
		}

		await request.onSuccess();
		closePopup();
	};

	return (
		<Panel
			growContent
			busy={busy}
			contentPadding="20px"
			label={msg("assignUserRole")}
			sx={{
				width: "calc(100vw - 32px)",
				maxWidth: "520px",
				maxHeight: "90vh",
				padding: "0",
				backgroundColor: "#fff",
				fontFamily: "Manrope, Euclid Circular B, sans-serif",
			}}
			decorator="absolute"
			onClose={closePopup}>
			<VBox gap="20px">
				<HBox gap="12px" sx={{ alignItems: "center", minWidth: 0 }}>
					<Box
						sx={{
							width: "44px",
							height: "44px",
							borderRadius: "12px",
							backgroundColor: "#f8f0ed",
							border: "1px solid #eadcd5",
							color: "#a8441f",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: "13px",
							fontWeight: 800,
							flexShrink: 0,
						}}>
						{initials || "US"}
					</Box>
					<VBox gap="2px" sx={{ flex: 1, minWidth: 0 }}>
						<Typo sx={{ color: "#1f1d1b", fontSize: "14px", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{userName}
						</Typo>
						<Typo variant="small" sx={{ color: "#7b746e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{userEmail || userLabel}
						</Typo>
					</VBox>
				</HBox>

				{error && <Error error={error} />}

				<VBox gap="8px">
					<Select<TUserRoleOption>
						label={msg("role")}
						value={selectedRole ?? undefined}
						values={roleOptions}
						disabled={!form || roleOptions.length === 0}
						onChange={role => {
							setSelectedRole(role);
							setError(undefined);
						}}
						sx={{ width: "100%" }}
					/>
					<Typo sx={{ color: "#7b746e", fontSize: "12px", lineHeight: "18px" }}>
						{selectedRole?.description || msg("theUserCanHaveOneRoleInTheCurrentProject")}
					</Typo>
				</VBox>

				{assignedRolesCount > 1 && (
					<Box sx={{ border: "1px solid #e5b65c", borderRadius: "8px", backgroundColor: "#fff8e8", padding: "10px 12px" }}>
						<Typo sx={{ color: "#7a5412", fontSize: "12px", lineHeight: "17px" }}>{msg("thisUserCurrentlyHas")}{assignedRolesCount}{msg("rolesChooseTheSingleRoleThatShouldRemain")}</Typo>
					</Box>
				)}

				<HBox gap="10px" sx={{ justifyContent: "flex-end", paddingTop: "16px", borderTop: "1px solid #eee8e4" }}>
					<Button variant="secondary" title={msg("cancel")} onClick={closePopup} />
					<Button
						variant="primary"
						title={msg("saveRole")}
						disabled={!form || !selectedRole || !hasRoleChanged}
						onClick={saveRole}
					/>
				</HBox>
			</VBox>
		</Panel>
	);
};
